import { memo, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { IClientOption } from '~/store/client/types';
import MenuList from '~/ui/components/inputs/SelectWithoutAnimation/components/MenuList';
import ClientMultiSelect from '~/ui/pages/Clients/reusable/ClientMultiSelect';

import { ClientWithAllocation } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/types';
import { normalizeClientOptionsByTeamToAllocations } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/EditClientAllocation/utils';
import useSelectAdditionalStyles from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/hooks/useSelectAdditionalStyles';
import { IAddAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/types';

interface IProps {
  loading: boolean;
  loadMore: VoidFunction;
  teamMemberIdx: number;
  updateAllocations: (selectedClients: ClientWithAllocation[]) => void;
}

const AllocationClientsSelectGU = ({
  loading,
  loadMore,
  teamMemberIdx,
  updateAllocations,
}: IProps): ReactElement => {
  const { watch } = useFormContext<IAddAllocationFormValues>();
  const { additionalStyleHandler } = useSelectAdditionalStyles(teamMemberIdx);

  const teamMembersAllocations = watch('teamMembersAllocations');

  const teamMemberAllocations = useMemo(
    () => teamMembersAllocations[teamMemberIdx],
    [teamMembersAllocations, teamMemberIdx],
  );

  const clientsAllocationsFields = useMemo(
    () => teamMemberAllocations.clientsAllocations,
    [teamMemberAllocations.clientsAllocations],
  );

  const groupedExistingClientsByClientId = useMemo(
    () => new Map(clientsAllocationsFields.map(field => [field.clientId, field])),
    [clientsAllocationsFields],
  );

  const handleSelectClientOptionsByTeam = useCallback(
    (options: IClientOption[]) => {
      const hasExistingOption = clientsAllocationsFields.some(field =>
        options.some(option => option.id === field.id),
      );
      let selectedOptions: Array<IClientOption | ClientWithAllocation> = [];

      if (options.length) {
        selectedOptions = hasExistingOption ? options : [...clientsAllocationsFields, ...options];
      }

      updateAllocations(
        normalizeClientOptionsByTeamToAllocations(selectedOptions, null).map(
          client => groupedExistingClientsByClientId.get(client.clientId) ?? client,
        ),
      );
    },
    [clientsAllocationsFields, groupedExistingClientsByClientId, updateAllocations],
  );

  return (
    <ClientMultiSelect
      loading={loading}
      loadMore={loadMore}
      label="Select Clients to Assign"
      customComponents={{ MenuList }}
      additionalStyleHandler={additionalStyleHandler}
      onSelect={handleSelectClientOptionsByTeam}
    />
  );
};

export default memo(AllocationClientsSelectGU);
